import type { OptionTypeBase, StylesConfig } from 'react-select';
import { ELocale } from '@/i18n/localeEnum';
import { options } from '@/components/resort/HeaderLangSelect/HeaderLangSelect';
import { getLocale } from '@/i18n/locale';

const locale = getLocale() as ELocale;
export const WIDGET_PAGE_PATH = 'widget';

// Re-exported for existing call sites; the constant itself lives in the
// edge-safe paths.ts so middleware can import it without dragging this
// module's React component / i18n imports into the Edge Runtime bundle.
export { IN_APP_LODGING_PATH } from '@/components/widget/paths';

// react-select (v4) has no className hooks, so it's themed through its
// `styles` API. Every color below is a design-system token from
// src/styles/globals.scss (the same ones the shadcn Input / SelectTrigger
// use), so the three selects on /widget match the rest of the form.
const token = (name: string) => `hsl(var(--${name}))`;
const radiusMd = 'calc(var(--radius) - 2px)';
const radiusSm = 'calc(var(--radius) - 4px)';

const optionBackground = (isSelected: boolean, isFocused: boolean): string => {
  if (isSelected) return token('primary');
  if (isFocused) return token('muted');
  return 'transparent';
};

export const selectStyles = <OptionType extends OptionTypeBase, IsMulti extends boolean = false>(): StylesConfig<OptionType, IsMulti> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '2.5rem',
    borderRadius: radiusMd,
    borderColor: state.isFocused ? token('ring') : token('input'),
    boxShadow: state.isFocused ? `0 0 0 1px ${token('ring')}` : 'none',
    backgroundColor: token('background'),
    fontSize: '0.875rem',
    transition: 'border-color 150ms, box-shadow 150ms',
    '&:hover': { borderColor: state.isFocused ? token('ring') : token('input') },
  }),
  valueContainer: (base) => ({ ...base, padding: '0.125rem 0.75rem' }),
  placeholder: (base) => ({ ...base, color: token('muted-foreground') }),
  singleValue: (base) => ({ ...base, color: token('foreground') }),
  input: (base) => ({ ...base, color: token('foreground') }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: token('border') }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? token('foreground') : token('muted-foreground'),
    '&:hover': { color: token('foreground') },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: token('muted-foreground'),
    '&:hover': { color: token('foreground') },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    overflow: 'hidden',
    borderRadius: radiusMd,
    border: `1px solid ${token('border')}`,
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    backgroundColor: token('popover'),
    color: token('popover-foreground'),
  }),
  menuList: (base) => ({ ...base, padding: '0.25rem' }),
  option: (base, state) => ({
    ...base,
    cursor: 'pointer',
    fontSize: '0.875rem',
    borderRadius: radiusSm,
    color: state.isSelected ? token('primary-foreground') : token('popover-foreground'),
    backgroundColor: optionBackground(state.isSelected, state.isFocused),
    '&:active': { backgroundColor: token('primary'), color: token('primary-foreground') },
  }),
  multiValue: (base) => ({ ...base, borderRadius: radiusSm, backgroundColor: token('muted') }),
  multiValueLabel: (base) => ({ ...base, color: token('foreground'), fontSize: '0.8125rem' }),
  multiValueRemove: (base) => ({
    ...base,
    color: token('muted-foreground'),
    '&:hover': { backgroundColor: token('destructive'), color: token('destructive-foreground') },
  }),
  noOptionsMessage: (base) => ({ ...base, fontSize: '0.875rem', color: token('muted-foreground') }),
  loadingMessage: (base) => ({ ...base, fontSize: '0.875rem', color: token('muted-foreground') }),
});

export const readableLanguagesNames: Record<ELocale, string> = {
  [ELocale.EN_US]: 'English (US)',
  [ELocale.EN_GB]: 'English (GB)',
  [ELocale.DE_DE]: 'Deutsch',
  [ELocale.FR_FR]: 'Français',
  [ELocale.IT_IT]: 'Italiano',
  [ELocale.PL_PL]: 'Polski',
  [ELocale.CS_CZ]: 'Čeština',
  [ELocale.SK_SK]: 'Slovenčina',
  [ELocale.NL_NL]: 'Nederlands',
  [ELocale.NN_NO]: 'Norsk',
  [ELocale.ES_ES]: 'Español',
  [ELocale.SV_SE]: 'Svenska',
  [ELocale.DA_DK]: 'Dansk',
};

export const defaultLanguageOption = { value: locale, label: readableLanguagesNames[locale] };

export type ResortQueryOpenStatus = 'open' | 'closed' | '';

export const languageOptions = options.map((i) => ({ value: i.value, label: readableLanguagesNames[i.value] }));
